import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@usehatchapp/material';
import React, { useEffect, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';

import { IntervalList, Response } from './components';

// converts double bracket snippet [[User Name]] to single brackets [User Name] for RTE
const doubleToSingle = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/\[\[/g, '[').replace(/\]\]/g, ']');
};

// converts single bracket snippet [User Name] to double brackets [[User Name]] for RTE
const singleToDouble = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/\[/g, '[[').replace(/\]/g, ']]');
};

const DEFAULT_RESPONSE = `Hey, thanks for reaching out to us. We are not in the office right now but we will get back to you [[Next Open Time]].
In the meantime, please give us any other information relevant to your project.`;

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const initialHoursToDays = {};
const initialHours = [];
DAYS_OF_WEEK.forEach((day, index) => {
  const interval = {
    close: { day: index, hour: 17, minute: 0 },
    open: { day: index, hour: 9, minute: 0 },
  };
  initialHoursToDays[day] = [interval];
  initialHours.push(interval);
});

// This strips any hour intervals with a certain day
const setDayToClosedHours = ({ index, hours }) => {
  return hours.filter((interval) => interval?.open?.day !== index);
};

export const AfterHours = () => {
  const [hours, setHours] = useState(initialHours);
  const [mappedHoursToDays, setMappedHoursToDays] =
    useState(initialHoursToDays);
  const [checked, setChecked] = useState(false);
  const [smsResponse, setSmsResponse] = useState(DEFAULT_RESPONSE);
  const [editing, setEditing] = useState(false);

  const {
    doUpdateOrganization,
    routeParams: { organizationId },
    currentOrganization,
  } = useConnect(
    'doUpdateOrganization',
    'selectRouteParams',
    'selectCurrentOrganization',
  );

  // Resets if you hit "cancel"
  const reset = () => {
    setHours(currentOrganization.businessHours);
    const { sendSMS, smsResponse: savedSmsResponse } =
      currentOrganization.afterHours;
    setChecked(sendSMS);
    setSmsResponse(singleToDouble(savedSmsResponse));
  };

  useEffect(() => {
    if (currentOrganization?.businessHours?.length) {
      const nextHours = [...currentOrganization.businessHours];
      setHours(nextHours);
    }
    if (currentOrganization?.afterHours) {
      const { sendSMS, smsResponse: savedSmsResponse } =
        currentOrganization.afterHours;
      setChecked(sendSMS);
      setSmsResponse(singleToDouble(savedSmsResponse));
    }
  }, [currentOrganization]);

  useEffect(() => {
    const nextMappedHoursToDays = {};
    // Sets up a lookup like { Sunday: [], Monday: [], ... }
    DAYS_OF_WEEK.forEach((day) => {
      nextMappedHoursToDays[day] = [];
    });
    hours.forEach(({ open, close }) => {
      // day is a 0-6 index of the day of the week
      const { day } = open;
      nextMappedHoursToDays[DAYS_OF_WEEK[day]].push({ close, open });
    });
    setMappedHoursToDays(nextMappedHoursToDays);
  }, [hours]);

  const onSave = () => {
    if (!organizationId) return null;
    const afterHours = {
      sendSMS: checked,
      smsResponse: doubleToSingle(smsResponse),
    };
    setEditing(false);
    return doUpdateOrganization({
      data: { ...currentOrganization, afterHours, businessHours: hours },
      organizationId,
    });
  };

  const onChangeChecked = () => {
    setChecked(!checked);
  };

  const onChangeSmsResponse = ({ target: { value } }) => {
    setSmsResponse(value);
  };

  // @TODO this seems really complicated. Why can't the API respond with something that's like
  // { Sunday: [], Monday: []} that's tied to the day of week, instead of nested?
  const onChangeHours = ({ hour, minute, day, kind, index }) => {
    // Which interval are you modifying? Since you can modify both at different times, grab if from the map
    const nextDayIntervals = [...mappedHoursToDays[day]];
    // Overwrite either 'open' or 'close' for that interval
    const dayIndex = DAYS_OF_WEEK.indexOf(day);
    const nextInterval = {
      ...nextDayIntervals[index],
      [kind]: { day: dayIndex, hour, minute },
    };
    nextDayIntervals[index] = nextInterval;
    // Remove that day then replace the values
    const nextHours = [
      ...setDayToClosedHours({ hours, index: dayIndex }),
      ...nextDayIntervals,
    ];
    setHours(nextHours);
  };

  const removeInterval = ({ open, close }) => {
    const nextHours = hours.filter(({ open: o, close: c }) => {
      return !(
        open.day === o.day &&
        open.hour === o.hour &&
        open.minute === o.minute &&
        close.hour === c.hour &&
        close.minute === c.minute
      );
    });
    setHours(nextHours);
  };

  return (
    <Card sx={{ mt: 2, width: '100%' }}>
      <CardContent>
        <Typography variant="overline">Business Hours</Typography>
        {!editing ? (
          <Button
            data-test-id="06d622ae-d281-4ee2-8799-cb232aea494a"
            size="small"
            sx={{ ml: 1 }}
            variant="text"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        ) : null}

        <IntervalList
          mappedHoursToDays={mappedHoursToDays}
          editing={editing}
          hours={hours}
          setHours={setHours}
          onChangeHours={onChangeHours}
          removeInterval={removeInterval}
        />

        <Response
          onChangeChecked={onChangeChecked}
          checked={checked}
          smsResponse={smsResponse}
          onChangeSmsResponse={onChangeSmsResponse}
          editing={editing}
        />
        {editing ? (
          <Grid container mt={1} alignItems="center">
            <Button
              data-test-id="bb8bcf56-1957-4abb-8d82-5612bde4213e"
              onClick={() => {
                setEditing(false);
                reset();
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              sx={{ ml: 1 }}
              data-test-id="23c8abf2-5865-44ad-a290-14ea03f5e14d"
              onClick={onSave}
              variant="contained"
            >
              Save
            </Button>
          </Grid>
        ) : null}
      </CardContent>
    </Card>
  );
};
